/**
 * Messung in der App: GTM-Container der Website (GTM-KRJ4N4LQ), dieselbe
 * Einwilligung, ein paar Ereignisse entlang des Einstiegs.
 *
 * WARUM JETZT: Bis zum 28.09.2026 hatte die App kein Tracking, mit der
 * Bedingung „erst klären, wer den Container veröffentlicht". Das ist geklärt
 * (Robert, 27.09.2026). GA4 sah bis dahin nur den Klick auf „Kostenlos
 * starten" auf der Website; was danach geschah, war unsichtbar.
 *
 * EINWILLIGUNG: Die Website schreibt die Cookie-Wahl in das Cookie
 * `cookie-consent-v2` für .99takes.com (99takes-website, Layout.astro). Die App
 * liest es. Nur wer noch nie gewählt hat, sieht hier einen Banner, und seine
 * Wahl landet im selben Cookie, gilt also auch auf der Website. Consent Mode
 * steht vor dem Laden auf „denied" und wird nur mit Einwilligung gehoben.
 *
 * DER CONTAINER LÄDT ERST NACH EINWILLIGUNG (Entscheidung Robert, 11.10.2026,
 * wie auf der Website): beim Laden von gtm.js geht die IP-Adresse an Google.
 * Ohne Analyse oder Marketing lädt nichts, auch keine cookielosen Signale.
 * Ereignisse, die vorher im dataLayer liegen, verarbeitet GTM beim Laden nach.
 *
 * WAS NICHT GESENDET WIRD: keine E-Mail-Adresse, keine Konto- oder Nutzer-id,
 * keine Texte. Nur Ereignisname und ein kurzer Parameter.
 *
 * NICHT AUF BERICHTEN: /bericht, /report und /connect laden keinen Container
 * und zeigen keinen Banner. Ein Bericht wird an den Kunden des Kunden
 * weitergeleitet; der soll dort keinen Cookie-Banner von uns sehen.
 */

export const GTM_ID = 'GTM-KRJ4N4LQ';

/** Oeffnet den Banner wieder (Fuss von AppRahmen). */
export const COOKIE_EINSTELLUNGEN = '99t-cookie-einstellungen';
export const oeffneCookieEinstellungen = () => window.dispatchEvent(new Event(COOKIE_EINSTELLUNGEN));
export const COOKIE = 'cookie-consent-v2';

export interface Einwilligung { analytics: boolean; marketing: boolean }

type Ereignis =
  | { event: 'register_submit' }
  // weg: 'code' seit 05.11.2026 (Code nach der Registrierung, CodeEingabe in Registrieren.tsx).
  | { event: 'sign_up'; method: 'email'; weg: 'bestaetigt' | 'nachgereicht' | 'code' }
  | { event: 'login'; method: 'email' }
  | { event: 'onboarding_done'; art: 'fertig' | 'uebersprungen' }
  // Ein Bildschirm des Einstiegs (seit 05.11.2026, wie im Tappla-Wizard):
  // 1 pruefen, 2 zielgruppe, 3 verbinden (im Rundgang rundgang_1 bis _4),
  // 4 bis 6 die Umfrage (hakt, rolle, quelle).
  | { event: 'onboarding_step'; step: number; step_name: string }
  | { event: 'key_created' }
  | { event: 'claude_connected' };

const OHNE_MESSUNG = ['/bericht', '/report', '/connect', '/verbinde'];

export const ohneMessung = (pfad: string) => OHNE_MESSUNG.some(p => pfad === p || pfad.startsWith(p + '/'));

declare global {
  interface Window { dataLayer?: unknown[] }
}

// ─── Cookie ─────────────────────────────────────────────────────────────────

/** document.cookie wirft in abgeschotteten Rahmen (SecurityError). Dann gilt: keine Wahl. */
function cookieLesen(): string {
  try { return typeof document === 'undefined' ? '' : document.cookie; } catch { return ''; }
}

export function leseEinwilligung(cookie = cookieLesen()): Einwilligung | null {
  const m = cookie.match(/(?:^|;\s*)cookie-consent-v2=([^;]*)/);
  if (!m) return null;
  try {
    const d = JSON.parse(decodeURIComponent(m[1]!));
    return { analytics: !!d?.analytics, marketing: !!d?.marketing };
  } catch {
    return null;
  }
}

/** Dasselbe Format wie die Website, damit beide Seiten es lesen. */
export function cookieText(e: Einwilligung, host: string, https: boolean, jetzt = new Date()): string {
  const wert = { necessary: true, analytics: e.analytics, marketing: e.marketing, version: '2.0', timestamp: jetzt.toISOString() };
  const domain = /(^|\.)99takes\.com$/.test(host) ? '; domain=.99takes.com' : '';
  return `${COOKIE}=${encodeURIComponent(JSON.stringify(wert))}; path=/; max-age=31536000; SameSite=Lax${https ? '; Secure' : ''}${domain}`;
}

// ─── Container ──────────────────────────────────────────────────────────────

function gtag(..._args: unknown[]) {
  // gtag() muss `arguments` pushen, kein Array: so erkennt der Container
  // Consent-Befehle.
  // eslint-disable-next-line prefer-rest-params
  (window.dataLayer = window.dataLayer || []).push(arguments);
}

const consentWerte = (e: Einwilligung | null) => ({
  analytics_storage: e?.analytics ? 'granted' : 'denied',
  functionality_storage: e?.analytics ? 'granted' : 'denied',
  ad_storage: e?.marketing ? 'granted' : 'denied',
  ad_user_data: e?.marketing ? 'granted' : 'denied',
  ad_personalization: e?.marketing ? 'granted' : 'denied',
});

let gestartet = false;
let geladen = false;
/** Die Wahl auf dieser Seite, falls das Cookie sich nicht schreiben liess. */
let gewaehlt: Einwilligung | null = null;
/** Merker von Ereignissen, die ohne Einwilligung in den dataLayer gingen. */
const offen = new Set<string>();

/** Darf der Container laden? Nur mit Analyse oder Marketing. */
export const darfLaden = (e: Einwilligung | null) => !!(e && (e.analytics || e.marketing));

function ladeContainer() {
  if (geladen) return;
  geladen = true;
  (window.dataLayer = window.dataLayer || []).push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  if (typeof location === 'undefined' || location.hostname !== 'run.99takes.com') return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  document.head.appendChild(s);
}

/**
 * Einmal je Seitenaufruf, und nur ausserhalb der Berichte. Der Container
 * laedt nur auf run.99takes.com: lokal und in Pages-Vorschauen landen die
 * Ereignisse im dataLayer (pruefbar), gehen aber nirgends hin.
 */
export function starteMessung(pfad: string) {
  if (gestartet || typeof window === 'undefined' || ohneMessung(pfad)) return;
  gestartet = true;
  window.dataLayer = window.dataLayer || [];
  gtag('consent', 'default', { ...consentWerte(null), security_storage: 'granted' });
  gtag('set', 'url_passthrough', true);
  gtag('set', 'ads_data_redaction', true);
  const e = leseEinwilligung();
  if (e) gtag('consent', 'update', consentWerte(e));
  if (darfLaden(e)) ladeContainer();
}

export function speichereEinwilligung(e: Einwilligung) {
  try {
    document.cookie = cookieText(e, location.hostname, location.protocol === 'https:');
  } catch {
    // Ohne Cookies gilt die Wahl nur fuer diese Seite; Consent Mode bekommt sie trotzdem.
  }
  gewaehlt = e;
  if (darfLaden(e)) merkeOffene();
  if (!gestartet) return;
  gtag('consent', 'update', consentWerte(e));
  if (darfLaden(e)) ladeContainer();
}

// ─── Ereignisse ─────────────────────────────────────────────────────────────

export function melde(e: Ereignis) {
  if (typeof window === 'undefined') return;
  (window.dataLayer = window.dataLayer || []).push({ ...e });
}

/**
 * Einmal je Gerät und Schlüssel. Fuer Ereignisse, die ein Konto nur einmal
 * hat (sign_up, claude_connected). Auf einem zweiten Geraet kann es noch
 * einmal kommen; ohne Datenbank-Zustand ist das die Grenze.
 *
 * DER MERKER NUR MIT EINWILLIGUNG (seit 26.10.2026, Entscheidung Robert): er
 * dient allein der Messung, ist also nicht unbedingt erforderlich (§ 25 Abs. 2
 * Nr. 2 TDDDG). Ohne Analyse oder Marketing wird nichts gespeichert; das
 * Ereignis liegt nur im dataLayer dieser Seite und geht nirgends hin, solange
 * der Container nicht laedt. Stimmt jemand auf derselben Seite noch zu,
 * verarbeitet GTM es beim Laden nach, und `merkeOffene` schreibt den Merker
 * dann, damit es beim naechsten Aufruf nicht noch einmal kommt.
 */
export function einmal(schluessel: string, e: Ereignis, einwilligung = gewaehlt ?? leseEinwilligung()) {
  const k = `99t-gemeldet:${schluessel}`;
  if (offen.has(k)) return;
  try {
    if (localStorage.getItem(k)) return;
  } catch {
    // Ohne Speicher lieber einmal zu oft als nie.
  }
  if (darfLaden(einwilligung)) merke(k);
  else offen.add(k);
  melde(e);
}

function merke(k: string) {
  try { localStorage.setItem(k, new Date().toISOString()); } catch { /* siehe oben */ }
}

function merkeOffene() {
  for (const k of offen) merke(k);
  offen.clear();
}

/** Wie lange nach der Registrierung eine Anmeldung noch als nachgereichter sign_up zählt. */
export const NACHREICHEN_TAGE = 7;

export const istFrisch = (erstellt: string | null | undefined, jetzt = Date.now()) =>
  !!erstellt && jetzt - Date.parse(erstellt) < NACHREICHEN_TAGE * 86_400_000;

/**
 * Der erste Aufruf aus Claude kommt nicht aus dem Browser. Die App erfaehrt ihn
 * beim naechsten Laden des Kontos (billing/status, erster_aufruf_at) und meldet
 * ihn dann, einmal je Konto und nur, wenn er frisch ist: ein altes Konto auf
 * einem neuen Geraet ist keine neue Verbindung.
 */
export function meldeVerbindung(s: { account_id?: string | null; erster_aufruf_at?: string | null } | null | undefined) {
  if (s?.account_id && istFrisch(s.erster_aufruf_at)) einmal(`claude_connected:${s.account_id}`, { event: 'claude_connected' });
}
